/**
 * Confirm Dialog Store
 *
 * A promise-based replacement for native confirm(). Call sites read almost
 * identically to the ones they replace:
 *
 *   if (!window.confirm(message)) return;   // before
 *   if (!(await confirmDialog({ message }))) return;   // after
 *
 * Requests are queued rather than replaced: every call must settle, otherwise
 * an awaiting handler would hang forever and leave its button stuck.
 */

import { create } from 'zustand';
import { forgetAtSessionEnd } from '../../lib/session-end';

interface ConfirmOptions {
  /** The question being asked. Already-localized text. */
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' styles the confirm button as destructive (delete, ban, revoke). */
  tone?: 'default' | 'danger';
}

interface PendingConfirm extends ConfirmOptions {
  id: number;
  resolve: (confirmed: boolean) => void;
}

interface ConfirmDialogState {
  queue: PendingConfirm[];
  /** Settle the dialog currently on screen and show the next one, if any. */
  settleCurrent: (confirmed: boolean) => void;
}

let nextConfirmId = 1;

export const useConfirmDialogStore = create<ConfirmDialogState>()((set, get) => ({
  queue: [],
  settleCurrent: (confirmed) => {
    const [current, ...rest] = get().queue;
    if (!current) return;
    set({ queue: rest });
    current.resolve(confirmed);
  },
}));

/**
 * Every question still asked, answered no and gone as a session ends, so the
 * next user in this tab is never asked the last one's question — and its
 * action never runs with their token. Its caller goes on as cancelled.
 */
forgetAtSessionEnd(() => {
  const { queue } = useConfirmDialogStore.getState();
  useConfirmDialogStore.setState({ queue: [] });
  for (const entry of queue) entry.resolve(false);
});

/**
 * Ask the user to confirm. Resolves true if they accept, false if they cancel,
 * dismiss with Escape, or click the backdrop.
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const entry: PendingConfirm = { ...options, id: nextConfirmId++, resolve };
    useConfirmDialogStore.setState((state) => ({ queue: [...state.queue, entry] }));
  });
}
